import React, { useMemo, useState } from "react";

const PricingAtfContent = ({
  vehicleData = {},
  location = "",
  make = "",
  model = "",
  minPrice = "",
  maxPrice = "",
  gvw = "",
  payload = "",
  range = "",
}) => {
  const content = useMemo(() => {
    const makeModel = make && model ? `${make} ${model}` : vehicleData?.name || "[Make Model]";
    const cityState = location || "[City, State]";
    const city = location?.split(",")?.[0]?.trim() || "[City]";
    const state = location?.split(",")?.[1]?.trim() || "[State]";
    const minPriceText = minPrice || vehicleData?.prices?.[location]?.minPrice || "[Min Price]";
    const maxPriceText = maxPrice || vehicleData?.prices?.[location]?.maxPrice || "[Max Price]";
    const gvwText = gvw || vehicleData?.gvw || "[GVW]";
    const payloadText = payload || vehicleData?.load || vehicleData?.payload || "[Payload]";
    const rangeText = range || vehicleData?.range || "[Range]";
    
    // Check if payload exists (not placeholder)
    const hasPayload = payloadText && payloadText !== "[Payload]";
    const gvwPayloadSentence = hasPayload 
      ? `The Gross Vehicle Weight (GVW) of ${gvwText} kg and ${payloadText} kg payload capacity makes it stand apart. `
      : "";

    const template = `About ${makeModel} price in ${cityState}
If you are planning to upgrade your fleet or start a new transport business, it is essential to know the latest ${makeModel} price in ${cityState} so that you can ensure profitability. The ${makeModel} has emerged as a leader in the three-wheeler segment. It provides the right balance of performance, low running cost, and high reliability. At present, the ${makeModel} ex-showroom price ${city}, ${state} is positioned competitively between ₹ ${minPriceText} Lakh and ₹ ${maxPriceText} Lakh, making it an accessible investment for individual drivers and fleet owners alike.
The ${makeModel} auto rickshaw price represents its class-leading engineering. It is designed and developed in such a way that it can easily handle the rough Indian roads. ${gvwPayloadSentence}Unlike some other electric vehicles, the ${makeModel} electric auto price includes the cost of the lithium-ion battery. This advanced battery delivers a real-world mileage of ${rangeText} km/charge. This kind of long range will help you in covering more trips per charge without worrying about recharging downtimes.
However, it is important to note and understand the difference between the ex-showroom price and the on-road price of an electric auto. The ${makeModel} on-road price ${cityState} is influenced by local factors such as RTO registration fees, insurance, and state-specific EV subsidies. This will help you in calculating the right budget for your business. While the ex-showroom price in ${city}, ${state} remains approximately similar, the final ${makeModel} on-road price can be reduced in many regions thanks to government incentives like the PM E-DRIVE scheme.
Why Choose Turno to Buy ${makeModel} in ${city}?
It is highly important to choose the right place to buy ${makeModel} in ${city}. At Turno, we provide a complete business ecosystem which is designed to ensure that your three-wheeler business is a constant "money-making machine."
When you check the ${makeModel} on-road price through our platform, you unlock a suite of exclusive benefits that traditional showrooms cannot match:
Lowest Down Payment & Fast Financing
We have various tie-ups with leading banks and NBFCs that ensure you get the lowest interest rates and quick loan approvals. At Turno, we make the ${makeModel} electric auto price even more affordable for your business.
Guaranteed Buyback Value
One of the biggest concerns with electric autos is uncertain resale value. Turno offers a Guaranteed Buyback policy, ensuring a high resale value for your vehicle after 3 years. This helps you protect your investment.
Turno One App
With the Turno One mobile app, you can easily manage your electric vehicle's health, track battery life, and monitor your earnings in real-time. This digital support helps you maintain the high efficiency that the ${makeModel} auto rickshaw price promises.
Verified New & Used Selection
Whether you want the latest model or a budget-friendly used vehicle, we provide a verified range of passenger and commercial three-wheelers tailored to your specific business needs in ${city}.
Therefore, choosing Turno gets you the most transparent ${makeModel} ex-showroom price ${cityState}, along with end-to-end support that covers everything from registration to long-term maintenance.`;

    return template;
  }, [vehicleData, location, make, model, minPrice, maxPrice, gvw, payload, range]);

  const paragraphs = content.split("\n").filter((p) => p.trim());
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="w-full md:w-[84%] mx-auto px-3 md:px-0 py-8 md:py-12">
      <div
        className={`relative overflow-hidden ${
          isExpanded ? "" : "max-h-[179px] md:max-h-[153px]"
        }`}
      >
        {!isExpanded && (
          <div
            className="absolute bottom-0 left-0 right-0 h-[60px]"
            style={{
              background: "linear-gradient(to bottom, transparent, white)",
            }}
          />
        )}
        {paragraphs.map((paragraph, index) => {
          // Check if it's a heading (starts with "About" or "Why Choose")
          const isHeading = paragraph.startsWith("About") || paragraph.startsWith("Why Choose");
          // Check if it's a subheading (short line that starts with specific keywords and doesn't end with period)
          const isSubheading =
            paragraph.length < 80 &&
            paragraph.length > 10 &&
            !paragraph.endsWith(".") &&
            (/^(Lowest|Guaranteed|Turno|Verified)/.test(paragraph) ||
              (paragraph.endsWith(":") && paragraph.length < 60));

          if (isHeading) {
            return (
              <h2
                key={index}
                className={`text-xl md:text-2xl font-bold text-gray-900 leading-[1.3] mb-6 md:mb-8 ${
                  index > 0 ? "mt-8 md:mt-10" : "mt-0"
                }`}
                style={{ fontFamily: '"Poppins", sans-serif' }}
              >
                {paragraph}
              </h2>
            );
          }

          if (isSubheading) {
            return (
              <h3
                key={index}
                className={`text-lg md:text-[22px] font-semibold text-black leading-[1.4] mb-2.5 md:mb-3.5 ${
                  index > 0 ? "mt-5 md:mt-7" : "mt-0"
                }`}
                style={{ fontFamily: '"Poppins", sans-serif' }}
              >
                {paragraph}
              </h3>
            );
          }

          return (
            <p
              key={index}
              className="text-sm md:text-base font-normal text-black leading-relaxed mb-3 md:mb-4"
              style={{ fontFamily: '"Poppins", sans-serif' }}
            >
              {paragraph}
            </p>
          );
        })}
      </div>
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="mt-2.5 text-[#024670] text-xs md:text-sm font-bold cursor-pointer inline-block hover:underline"
      >
        {isExpanded ? "Read Less" : "Read More"}
      </button>
    </div>
  );
};

export default PricingAtfContent;
